import React from 'react';
import { buildResponsiveClasses } from '../../utils/responsive';

const LoadingSpinner = ({ 
  size = 'md', 
  message, 
  className = '',
  variant = 'spinner',
  color = 'blue'
}) => {
  const sizeClasses = {
    xs: 'h-4 w-4',
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16'
  };

  const colorClasses = {
    blue: 'border-blue-500',
    gray: 'border-gray-500',
    green: 'border-green-500',
    red: 'border-red-500',
    yellow: 'border-yellow-500'
  };

  const spinnerClasses = buildResponsiveClasses({
    base: `
      animate-spin rounded-full border-2 border-gray-200
      ${colorClasses[color]} border-t-transparent
      ${sizeClasses[size]}
    `,
    custom: className
  });

  if (variant === 'dots') {
    return (
      <div className={`flex items-center justify-center space-x-1 ${className}`}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`
              ${sizeClasses[size]} rounded-full bg-${color}-500
              animate-pulse
            `}
            style={{
              animationDelay: `${i * 0.2}s`,
              animationDuration: '1s'
            }}
          />
        ))}
        {message && (
          <span className="ml-3 text-sm text-text-secondary" aria-live="polite">
            {message}
          </span>
        )}
      </div>
    );
  }

  if (variant === 'pulse') {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <div className={`
          ${sizeClasses[size]} rounded-full bg-${color}-500
          animate-pulse
        `} />
        {message && (
          <span className="ml-3 text-sm text-text-secondary" aria-live="polite">
            {message}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-center ${className}`} role="status" aria-live="polite">
      <div className={spinnerClasses} aria-hidden="true" />
      {message && (
        <span className="ml-3 text-sm text-text-secondary">
          {message}
        </span>
      )}
      <span className="sr-only">Loading...</span>
    </div>
  );
};

// Skeleton loading components for different content types
LoadingSpinner.SkeletonCard = ({ className = '', lines = 3 }) => (
  <div className={`animate-pulse ${className}`}>
    <div className="bg-card-bg rounded-lg shadow-sm border border-card-border p-6">
      <div className="h-4 bg-bg-tertiary rounded w-3/4 mb-4"></div>
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="space-y-2">
          <div className="h-3 bg-bg-tertiary rounded"></div>
          <div className="h-3 bg-bg-tertiary rounded w-5/6"></div>
        </div>
      ))}
    </div>
  </div>
);
LoadingSpinner.SkeletonCard.displayName = 'LoadingSpinner.SkeletonCard';

LoadingSpinner.SkeletonList = ({ items = 3, className = '' }) => (
  <div className={`space-y-4 ${className}`}>
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="animate-pulse">
        <div className="flex items-center space-x-4 p-4 bg-card-bg rounded-lg shadow-sm border border-card-border">
          <div className="h-10 w-10 bg-bg-tertiary rounded-full"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-bg-tertiary rounded w-3/4"></div>
            <div className="h-3 bg-bg-tertiary rounded w-1/2"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);
LoadingSpinner.SkeletonList.displayName = 'LoadingSpinner.SkeletonList';

LoadingSpinner.SkeletonForm = ({ fields = 4, className = '' }) => (
  <div className={`space-y-6 ${className}`}>
    {Array.from({ fields }).map((_, i) => (
      <div key={i} className="animate-pulse">
        <div className="h-4 bg-bg-tertiary rounded w-1/4 mb-2"></div>
        <div className="h-10 bg-bg-tertiary rounded"></div>
      </div>
    ))}
  </div>
);
LoadingSpinner.SkeletonForm.displayName = 'LoadingSpinner.SkeletonForm';

LoadingSpinner.SkeletonTable = ({ rows = 5, columns = 4, className = '' }) => (
  <div className={`animate-pulse ${className}`}>
    <div className="bg-card-bg rounded-lg shadow-sm border border-card-border overflow-hidden">
      {/* Header */}
      <div className="bg-bg-secondary px-6 py-3 border-b border-card-border">
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: columns }).map((_, i) => (
            <div key={i} className="h-4 bg-bg-tertiary rounded"></div>
          ))}
        </div>
      </div>
      
      {/* Rows */}
      <div className="divide-y divide-card-border">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="px-6 py-4">
            <div className="grid grid-cols-4 gap-4">
              {Array.from({ length: columns }).map((_, j) => (
                <div key={j} className="h-4 bg-bg-tertiary rounded"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
LoadingSpinner.SkeletonTable.displayName = 'LoadingSpinner.SkeletonTable';

LoadingSpinner.SkeletonStats = ({ stats = 4, className = '' }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 ${className}`}>
    {Array.from({ length: stats }).map((_, i) => (
      <div key={i} className="animate-pulse">
        <div className="bg-card-bg rounded-lg shadow-sm border border-card-border p-5">
          <div className="flex items-center">
            <div className="h-6 w-6 bg-bg-tertiary rounded"></div>
            <div className="ml-5 flex-1">
              <div className="h-4 bg-bg-tertiary rounded w-3/4 mb-2"></div>
              <div className="h-6 bg-bg-tertiary rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);
LoadingSpinner.SkeletonStats.displayName = 'LoadingSpinner.SkeletonStats';

// Full page loading component
LoadingSpinner.FullPage = ({ message = "Loading..." }) => (
  <div className="min-h-screen flex items-center justify-center bg-bg-primary">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-lg text-text-secondary">{message}</p>
    </div>
  </div>
);
LoadingSpinner.FullPage.displayName = 'LoadingSpinner.FullPage';

// Inline loading for buttons
LoadingSpinner.Button = ({ size = 'sm', className = '' }) => (
  <LoadingSpinner 
    size={size} 
    className={`inline-block ${className}`}
    color="white"
  />
);
LoadingSpinner.Button.displayName = 'LoadingSpinner.Button';

export default LoadingSpinner;